import React, { useCallback, useEffect, useState } from "react";
import api from "services/api";
import { __ } from "services/Translator";
import AuthGate from "components/auth/AuthGate";
import RewardsContent from "components/rewards/RewardsContent";
import RewardsTeaser from "components/rewards/RewardsTeaser";
import { normalizeRewardsDashboard } from "components/rewards/rewardsNormalize";
import { getRewardsMockState } from "components/rewards/rewardsMockData";

// api.get never rejects — it resolves a wrapped {status,data}. Only an explicit
// "feature not live yet" signal (route missing / not enabled) may fall back to
// the preview/demo dashboard. Every other non-200 is a genuine error and must
// NEVER render fabricated balances to a real logged-in player.
function isNotEnabledResponse(response) {
  if (response.status === 404) return true;
  const payload = response.data?.data ?? response.data;
  return payload?.feature_enabled === false || payload?.status === "not_enabled";
}

function GhostBlock({ style }) {
  return <span className="gmf-skel" aria-hidden="true" style={style} />;
}

function RewardsSkeleton() {
  const block = {
    display: "block",
    background: "var(--felt-700)",
    border: "1px solid var(--felt-line-2)",
    borderRadius: 12,
  };
  const line = (w, h = 12) => ({ ...block, width: w, height: h, borderRadius: 999 });

  return (
    <div
      className="rewards-page container-xxl gmf-page-skeleton"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {__("Loading your rewards…")}
      </span>

      <GhostBlock style={{ ...line("40%", 26), margin: "6px 0 20px" }} />

      <div
        className="gmf-skel-vip"
        style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}
      >
        <GhostBlock
          style={{ ...block, width: 64, height: 64, borderRadius: "50%", flex: "0 0 auto" }}
        />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <GhostBlock style={line("55%")} />
          <GhostBlock style={{ ...block, width: "100%", height: 10, borderRadius: 999 }} />
          <GhostBlock style={line("35%", 10)} />
        </div>
      </div>

      <div
        className="gmf-skel-balances"
        style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}
      >
        {[0, 1, 2].map((i) => (
          <GhostBlock
            key={i}
            style={{ ...block, flex: "1 1 180px", minWidth: 0, height: 118 }}
          />
        ))}
      </div>

      <GhostBlock
        style={{ ...block, width: "100%", height: 84, marginBottom: 24 }}
      />

      <GhostBlock style={{ ...line("30%", 16), marginBottom: 16 }} />
      <div
        className="gmf-skel-badges"
        style={{ display: "flex", gap: 14, flexWrap: "wrap" }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <GhostBlock
            key={i}
            style={{ ...block, width: 72, height: 72, borderRadius: "50%" }}
          />
        ))}
      </div>
    </div>
  );
}

function RewardsError({ onRetry }) {
  return (
    <div className="rewards-page container-xxl rewards-page--error" role="alert">
      <div className="rewards-page__error-panel">
        {/* Emoji fallback until the clover-with-bandage illustration ships (see asset brief). */}
        <div className="rewards-page__error-art" aria-hidden="true">
          🍀
        </div>
        <h1 className="rewards-page__error-title">
          {__("We couldn't load your rewards")}
        </h1>
        <p className="rewards-page__error-copy">
          {__(
            "Your rewards are safe — this is just a hiccup on our side. Please try again."
          )}
        </p>
        <button
          type="button"
          className="rewards-page__error-retry"
          onClick={onRetry}
        >
          {__("Try again")}
        </button>
      </div>
    </div>
  );
}

// Authenticated dashboard. Mounted ONLY when the visitor is logged in (AuthGate
// never renders children for a guest), so the personal /rewards/dashboard call
// can never fire for a stranger and no real player's balances can leak.
function RewardsDashboard() {
  // loading | ready | preview | error
  const [status, setStatus] = useState("loading");
  const [dashboard, setDashboard] = useState(null);

  const loadDashboard = useCallback(async () => {
    setStatus("loading");
    const response = await api.get("/rewards/dashboard");

    if (response.status === 200 && response.data?.data) {
      setDashboard(normalizeRewardsDashboard(response.data.data));
      setStatus("ready");
      return;
    }
    if (isNotEnabledResponse(response)) {
      setDashboard(getRewardsMockState());
      setStatus("preview");
      return;
    }
    setDashboard(null);
    setStatus("error");
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (status === "loading") {
    return <RewardsSkeleton />;
  }

  if (status === "error") {
    return <RewardsError onRetry={loadDashboard} />;
  }

  return (
    <RewardsContent data={dashboard} isPreviewDemo={status === "preview"} />
  );
}

export default function Rewards(props) {
  return (
    <AuthGate teaser={<RewardsTeaser {...props} />}>
      <RewardsDashboard />
    </AuthGate>
  );
}
